export const isWebAuthnSupported = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'credentials' in navigator &&
    'create' in navigator.credentials &&
    'get' in navigator.credentials
  );
};

const base64UrlEncode = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const base64UrlDecode = (base64url: string): ArrayBuffer => {
  const normalized = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(normalized + padding);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return bytes.buffer;
};

/** Wire-format options from the API (base64url strings, not ArrayBuffers). */
type WireWebAuthnOptions = {
  [key: string]: unknown;
};

type CredentialJSON = {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment?: string;
  response: {
    clientDataJSON?: string;
    attestationObject?: string;
    authenticatorData?: string;
    signature?: string;
    userHandle?: string;
  };
};

const credentialToJSON = (credential: PublicKeyCredential): CredentialJSON => {
  const response = credential.response as AuthenticatorAttestationResponse | AuthenticatorAssertionResponse;

  const result: CredentialJSON = {
    id: credential.id,
    rawId: base64UrlEncode(credential.rawId),
    type: credential.type,
    response: {}
  };

  if (credential.authenticatorAttachment) {
    result.authenticatorAttachment = credential.authenticatorAttachment;
  }

  if ('attestationObject' in response) {
    result.response.clientDataJSON = base64UrlEncode(response.clientDataJSON);
    result.response.attestationObject = base64UrlEncode(response.attestationObject);
  }

  if ('authenticatorData' in response) {
    result.response.clientDataJSON = base64UrlEncode(response.clientDataJSON);
    result.response.authenticatorData = base64UrlEncode(response.authenticatorData);
    result.response.signature = base64UrlEncode(response.signature);
    if (response.userHandle) {
      result.response.userHandle = base64UrlEncode(response.userHandle);
    }
  }

  return result;
};

const decodeBufferSource = (value: unknown): BufferSource => {
  if (typeof value === 'string') {
    return base64UrlDecode(value);
  }
  return value as BufferSource;
};

const prepareCreationOptions = (options: WireWebAuthnOptions): PublicKeyCredentialCreationOptions => {
  const wire = options as unknown as PublicKeyCredentialCreationOptions & {
    challenge: string | BufferSource;
    user: PublicKeyCredentialUserEntity & { id: string | BufferSource };
    excludeCredentials?: Array<PublicKeyCredentialDescriptor & { id: string | BufferSource }>;
  };

  const publicKey: PublicKeyCredentialCreationOptions = {
    ...wire,
    challenge: decodeBufferSource(wire.challenge),
    user: {
      ...wire.user,
      id: decodeBufferSource(wire.user.id)
    }
  };

  if (wire.excludeCredentials) {
    publicKey.excludeCredentials = wire.excludeCredentials.map((cred) => ({
      ...cred,
      id: decodeBufferSource(cred.id)
    }));
  }

  return publicKey;
};

const prepareRequestOptions = (options: WireWebAuthnOptions): PublicKeyCredentialRequestOptions => {
  const wire = options as unknown as PublicKeyCredentialRequestOptions & {
    challenge: string | BufferSource;
    allowCredentials?: Array<PublicKeyCredentialDescriptor & { id: string | BufferSource }>;
  };

  const publicKey: PublicKeyCredentialRequestOptions = {
    ...wire,
    challenge: decodeBufferSource(wire.challenge)
  };

  if (wire.allowCredentials) {
    publicKey.allowCredentials = wire.allowCredentials.map((cred) => ({
      ...cred,
      id: decodeBufferSource(cred.id)
    }));
  }

  return publicKey;
};

export const createPasskey = async (options: WireWebAuthnOptions): Promise<PublicKeyCredential> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser');
  }

  const publicKey = prepareCreationOptions(options);

  try {
    const credential = await navigator.credentials.create({ publicKey });

    if (!credential || !(credential instanceof PublicKeyCredential)) {
      throw new Error('Failed to create credential');
    }

    return credential;
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'NotAllowedError') {
        throw new Error('User cancelled passkey registration', { cause: error });
      }
      throw error;
    }
    throw new Error('Failed to create passkey', { cause: error });
  }
};

export const authenticatePasskey = async (options: WireWebAuthnOptions): Promise<PublicKeyCredential> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser');
  }

  const publicKey = prepareRequestOptions(options);

  try {
    const credential = await navigator.credentials.get({ publicKey });

    if (!credential || !(credential instanceof PublicKeyCredential)) {
      throw new Error('Failed to get credential');
    }

    return credential;
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'NotAllowedError') {
        throw new Error('User cancelled passkey authentication', { cause: error });
      }
      throw error;
    }
    throw new Error('Failed to authenticate with passkey', { cause: error });
  }
};

export const buildRegistrationPayload = (challengeSid: string, credential: PublicKeyCredential) => {
  const json = credentialToJSON(credential);
  const { clientDataJSON, attestationObject } = json.response;
  if (!clientDataJSON || !attestationObject) {
    throw new Error('Incomplete credential response from authenticator');
  }

  return {
    challengeSid,
    credential: {
      id: json.id,
      rawId: json.rawId,
      type: json.type,
      authenticatorAttachment: json.authenticatorAttachment,
      response: { clientDataJSON, attestationObject }
    }
  };
};

export const buildAuthenticationPayload = (challengeSid: string, credential: PublicKeyCredential) => {
  const json = credentialToJSON(credential);
  const { clientDataJSON, authenticatorData, signature, userHandle } = json.response;
  if (!clientDataJSON || !authenticatorData || !signature) {
    throw new Error('Incomplete credential assertion from authenticator');
  }

  return {
    challengeSid,
    credential: {
      id: json.id,
      rawId: json.rawId,
      type: json.type,
      authenticatorAttachment: json.authenticatorAttachment,
      response: {
        authenticatorData,
        clientDataJSON,
        signature,
        ...(userHandle ? { userHandle } : {})
      }
    }
  };
};
